import {
  optionalEmail,
  optionalString,
  requiredEmail,
  REQUIRED_MESSAGE,
  requiredNumber,
  requiredString,
} from '@wyz/utils';
import { z } from 'zod';
import { QUOTE_DISPLAY_TYPE } from '~/common/schemas';
import { CIVILITY, RIM_TYPE_ENUM } from '~/purchase/schemas';
import { IQuoteSetting } from '~/purchase/types';

export const contactSchema = z.object({
  registration: optionalString,
  firstName: optionalString,
  lastName: optionalString,
  phone: optionalString,
  email: optionalEmail,
  company: optionalString,
  address: optionalString,
  zipCode: optionalString,
  city: optionalString,
  gdprAccepted: z.boolean(),
  civility: z.enum([CIVILITY.MR, CIVILITY.MRS]).nullish(),
});

export const quoteCartSchema = z.object({
  products: z.record(
    z.string(),
    z.object({
      quantity: z
        .number({
          error: (issue) =>
            issue.input === undefined ? REQUIRED_MESSAGE : REQUIRED_MESSAGE,
        })
        .min(1, REQUIRED_MESSAGE),
    }),
  ),
  contact: contactSchema,
  validityDays: z.number(),
  comment: z.string(),
  rimType: z.enum([RIM_TYPE_ENUM.STEEL, RIM_TYPE_ENUM.ALLOY]).nullish(),
});

// Which contact fields are mandatory is driven by the PDV's quote display settings, so the shape
// has to be built per-setting. Kept here so the cart form and the recipient modal cannot drift.
export const generateQuoteContactSchema = (setting?: IQuoteSetting) =>
  z.object({
    registration: setting?.vehicleRegistrationRequired
      ? requiredString
      : optionalString,
    firstName: setting?.fullNameRequired ? requiredString : optionalString,
    lastName: setting?.fullNameRequired ? requiredString : optionalString,
    phone: setting?.phoneNumberRequired ? requiredString : optionalString,
    email: setting?.emailRequired ? requiredEmail : optionalEmail,
    company: optionalString,
    address: optionalString,
    zipCode: optionalString,
    city: optionalString,
    gdprAccepted: z.boolean(),
    civility: z.enum([CIVILITY.MR, CIVILITY.MRS]).nullish(),
  });

export const generateQuoteSettingSchema = (setting?: IQuoteSetting) => {
  return z.object({
    products: z.record(
      z.string(),
      z.object({
        quantity: z
          .number({
            error: (issue) =>
              issue.input === undefined ? REQUIRED_MESSAGE : REQUIRED_MESSAGE,
          })
          .min(1, REQUIRED_MESSAGE),
      }),
    ),
    contact: generateQuoteContactSchema(setting),
    validityDays: z.number(),
    comment: z.string(),
    rimType: z.enum([RIM_TYPE_ENUM.STEEL, RIM_TYPE_ENUM.ALLOY]).nullish(),
  });
};

const basePriceEditFormSchema = z.object({
  quantity: requiredNumber,
  discountedPriceTaxIncl: requiredNumber,
  discount: z.number().nullish(),
  margin: requiredNumber,
});

export const servicePriceEditFormSchema = z.object({
  item: basePriceEditFormSchema,
});

export const itemPriceEditFormSchema = z.object({
  items: z.record(z.string(), basePriceEditFormSchema),
  mountingPrice: z.number().nullish(),
});

export const quoteSettingsFormSchema = z.object({
  showBfDiscount: z.boolean(),
  displayType: z.enum(QUOTE_DISPLAY_TYPE),
  validityDays: z.number().min(1, REQUIRED_MESSAGE).max(60, REQUIRED_MESSAGE),
  additionalMessage: z
    .string()
    .max(256, 'app.common.assert.err_value_too_long'),
  emailRequired: z.boolean(),
  fullNameRequired: z.boolean(),
  phoneNumberRequired: z.boolean(),
  vehicleRegistrationRequired: z.boolean(),
  gdpr1: z.string().max(1000, 'app.common.assert.err_value_too_long'),
  termsOfUse: z
    .string()
    .max(1024, 'app.common.assert.err_value_too_long')
    .nullish(),
  paymentTerms: z.string().max(1024, 'app.common.assert.err_value_too_long'),
  segments: z
    .array(
      z.object({
        publicKey: z.string(),
        name: z.string(),
        id: z.string(),
      }),
    )
    .max(4, 'app.common.assert.err_array_max_elements'),
  seasonIds: z.array(z.number()).min(1, REQUIRED_MESSAGE),
  extendToHigherLoadSpeedIndexes: z.boolean(),
});
